import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  TOOLS as SEED_TOOLS,
  TOOL_CATEGORIES as SEED_TOOL_CATEGORIES,
  QUICK_PICKS as SEED_QUICK_PICKS,
} from "@/data/tools";
import {
  ARTICLES as SEED_ARTICLES,
  ARTICLE_CATEGORIES as SEED_ARTICLE_CATEGORIES,
} from "@/data/articles";

/**
 * ContentProvider
 *
 * Single source of truth for the two CMS-backed content types (tools + articles).
 * The admin dashboard writes to MongoDB via /api/admin/{tools,articles}…, and
 * the public pages read the exact same data via /api/{tools,articles}.
 *
 * This provider:
 *   - fetches tools + articles + categories + quick picks once at mount
 *   - falls back to the bundled static seed files while loading or on network
 *     failure so the public site never shows an empty state after a deploy
 *   - re-fetches on demand via `refresh(tools|articles|all)`
 *
 * Consumers use the `useTools()` / `useArticles()` hooks which preserve the
 * same helper functions the old static files exported (`getToolBySlug`,
 * `primaryCategoryOf`, `getArticleBySlug`, `getArticleCategory`).
 */

// API calls use relative `/api/...` paths so the browser hits whatever
// origin it's currently on (preview, production, custom domain). This
// avoids baking a build-time hostname into the bundle that may not match
// the runtime origin (which previously caused "We couldn't reach the
// server" errors on the live `syrianaisociety.com` domain).
const ContentContext = createContext(null);

/* --------------------------- helpers (static shape) ------------------------ */

// Normalise quick-picks from the API to the shape the public <StartHere />
// component has always consumed: `{ id, need:{ar,en}, tools:[toolSlug] }`.
function normalizeQuickPicksFromApi(apiItems) {
  if (!Array.isArray(apiItems)) return [];
  return apiItems.map((q) => ({
    id: q.id,
    need: q.need || { ar: "", en: "" },
    tools: q.tool_slug ? [q.tool_slug] : [],
  }));
}

// Build a fast lookup of category metadata by id (works for both tools and articles).
function indexById(list) {
  const map = new Map();
  (list || []).forEach((c) => {
    if (c && c.id) map.set(c.id, c);
  });
  return map;
}

/* --------------------------- Provider -------------------------------------- */

export function ContentProvider({ children }) {
  const [tools, setTools] = useState(SEED_TOOLS);
  const [toolCategories, setToolCategories] = useState(SEED_TOOL_CATEGORIES);
  const [quickPicks, setQuickPicks] = useState(SEED_QUICK_PICKS);
  const [toolsLoaded, setToolsLoaded] = useState(false);

  const [articles, setArticles] = useState(SEED_ARTICLES);
  const [articleCategories, setArticleCategories] = useState(SEED_ARTICLE_CATEGORIES);
  const [articlesLoaded, setArticlesLoaded] = useState(false);

  const loadTools = useCallback(async () => {
    try {
      const res = await fetch(`/api/tools`, { credentials: "include" });
      if (!res.ok) throw new Error(`tools:${res.status}`);
      const data = await res.json();
      if (Array.isArray(data.tools)) setTools(data.tools);
      if (Array.isArray(data.categories)) setToolCategories(data.categories);
      setQuickPicks(normalizeQuickPicksFromApi(data.quick_picks || []));
    } catch {
      // Keep seed fallback in place; do not clobber the UI.
    } finally {
      setToolsLoaded(true);
    }
  }, []);

  const loadArticles = useCallback(async () => {
    try {
      const res = await fetch(`/api/articles`, { credentials: "include" });
      if (!res.ok) throw new Error(`articles:${res.status}`);
      const data = await res.json();
      if (Array.isArray(data.articles)) setArticles(data.articles);
      if (Array.isArray(data.categories)) setArticleCategories(data.categories);
    } catch {
      // Fall back to seed data silently.
    } finally {
      setArticlesLoaded(true);
    }
  }, []);

  const refresh = useCallback(
    (scope = "all") => {
      const jobs = [];
      if (scope === "all" || scope === "tools") jobs.push(loadTools());
      if (scope === "all" || scope === "articles") jobs.push(loadArticles());
      return Promise.all(jobs);
    },
    [loadTools, loadArticles]
  );

  useEffect(() => {
    loadTools();
    loadArticles();
  }, [loadTools, loadArticles]);

  const toolCategoryIndex = useMemo(() => indexById(toolCategories), [toolCategories]);
  const articleCategoryIndex = useMemo(() => indexById(articleCategories), [articleCategories]);

  /* ----------- Helpers returned to consumers (stable refs per data) -------- */

  const tools_helpers = useMemo(
    () => ({
      tools,
      toolCategories,
      quickPicks,
      toolsLoaded,
      getToolBySlug: (slug) => tools.find((t) => t.slug === slug) || null,
      getToolsByCategory: (cat) =>
        tools.filter((t) => Array.isArray(t.categories) && t.categories.includes(cat)),
      getToolCategory: (id) => toolCategoryIndex.get(id) || null,
      primaryCategoryOf: (tool) =>
        toolCategoryIndex.get((tool?.categories && tool.categories[0]) || "") || null,
    }),
    [tools, toolCategories, quickPicks, toolsLoaded, toolCategoryIndex]
  );

  const articles_helpers = useMemo(
    () => ({
      articles,
      articleCategories,
      articlesLoaded,
      getArticleBySlug: (slug) => articles.find((a) => a.slug === slug) || null,
      getArticleCategory: (id) => articleCategoryIndex.get(id) || null,
    }),
    [articles, articleCategories, articlesLoaded, articleCategoryIndex]
  );

  const value = useMemo(
    () => ({ ...tools_helpers, ...articles_helpers, refresh }),
    [tools_helpers, articles_helpers, refresh]
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) {
    // Let pages render with seed fallbacks even without the provider (tests,
    // isolated storybook-like mounts), so nothing explodes.
    return {
      tools: SEED_TOOLS,
      toolCategories: SEED_TOOL_CATEGORIES,
      quickPicks: SEED_QUICK_PICKS,
      toolsLoaded: false,
      articles: SEED_ARTICLES,
      articleCategories: SEED_ARTICLE_CATEGORIES,
      articlesLoaded: false,
      getToolBySlug: (slug) => SEED_TOOLS.find((t) => t.slug === slug) || null,
      getToolsByCategory: (cat) =>
        SEED_TOOLS.filter((t) => t.categories && t.categories.includes(cat)),
      getToolCategory: (id) => SEED_TOOL_CATEGORIES.find((c) => c.id === id) || null,
      primaryCategoryOf: (tool) =>
        SEED_TOOL_CATEGORIES.find((c) => c.id === (tool?.categories?.[0])) || null,
      getArticleBySlug: (slug) => SEED_ARTICLES.find((a) => a.slug === slug) || null,
      getArticleCategory: (id) => SEED_ARTICLE_CATEGORIES.find((c) => c.id === id) || null,
      refresh: async () => {},
    };
  }
  return ctx;
}

/* Small convenience hooks that mirror the old module-level imports. */
export function useTools() {
  const c = useContent();
  return {
    TOOLS: c.tools,
    TOOL_CATEGORIES: c.toolCategories,
    QUICK_PICKS: c.quickPicks,
    toolsLoaded: c.toolsLoaded,
    getToolBySlug: c.getToolBySlug,
    getToolsByCategory: c.getToolsByCategory,
    getToolCategory: c.getToolCategory,
    primaryCategoryOf: c.primaryCategoryOf,
  };
}

export function useArticles() {
  const c = useContent();
  return {
    ARTICLES: c.articles,
    ARTICLE_CATEGORIES: c.articleCategories,
    articlesLoaded: c.articlesLoaded,
    getArticleBySlug: c.getArticleBySlug,
    getArticleCategory: c.getArticleCategory,
  };
}
